import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Breadcrumbs,
  Link,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Folder,
  InsertDriveFile,
  Link as LinkIcon,
  FolderOpen,
  NavigateNext,
  ArrowBack,
  CheckCircle,
  CheckCircleOutline,
} from '@mui/icons-material';

const FolderBrowserStep = ({
  loading,
  allItems,
  pathStack,
  selectedFolder,
  level1Items,
  itemMode,
  browseMode,
  directPathInput,
  mode,
  onBrowseModeChange,
  onNavigateInto,
  onBreadcrumbNavigate,
  onSelectFolder,
  onSelectCurrentFolder,
  onDirectPathInputChange,
  onUseDirectPath,
  onClearSelectedFolder,
}) => {
  const fileItems = allItems.filter(i => !i.isFolder);
  const folderItems = allItems.filter(i => i.isFolder);
  const breadcrumbs = [{ path: '/', name: 'Inicio' }, ...pathStack];

  return (
    <Box>
      {/* Mode toggle */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Chip
          icon={<FolderOpen fontSize="small" />}
          label="Navegar carpetas"
          onClick={() => onBrowseModeChange('navigate')}
          color={browseMode === 'navigate' ? 'primary' : 'default'}
          variant={browseMode === 'navigate' ? 'filled' : 'outlined'}
        />
        <Chip
          icon={<LinkIcon fontSize="small" />}
          label="Pegar ruta directa"
          onClick={() => onBrowseModeChange('url')}
          color={browseMode === 'url' ? 'primary' : 'default'}
          variant={browseMode === 'url' ? 'filled' : 'outlined'}
        />
      </Box>

      {/* Selected folder badge */}
      {selectedFolder && (
        <Alert
          severity="success"
          icon={<CheckCircle />}
          sx={{ mb: 2 }}
          action={
            <Button size="small" color="inherit" onClick={onClearSelectedFolder}>
              Cambiar
            </Button>
          }
        >
          Carpeta seleccionada: <strong>{selectedFolder.name}</strong>
          {mode === 'new' && level1Items.length > 0 && (
            <span> — {level1Items.length} {itemMode === 'folders' ? 'subcarpetas' : 'archivos'} encontrados</span>
          )}
        </Alert>
      )}

      {browseMode === 'url' ? (
        <UrlInput
          value={directPathInput}
          onChange={onDirectPathInputChange}
          onSubmit={onUseDirectPath}
        />
      ) : (
        <FolderNavigator
          loading={loading}
          breadcrumbs={breadcrumbs}
          pathStack={pathStack}
          folderItems={folderItems}
          fileItems={fileItems}
          allItems={allItems}
          onBreadcrumbNavigate={onBreadcrumbNavigate}
          onSelectCurrentFolder={onSelectCurrentFolder}
          onNavigateInto={onNavigateInto}
          onSelectFolder={onSelectFolder}
        />
      )}
    </Box>
  );
};

const UrlInput = ({ value, onChange, onSubmit }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      Ingresa la ruta de la carpeta en OneDrive (ej: <code>/Documents/MiCarpeta</code>)
    </Typography>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <TextField
        fullWidth
        size="small"
        placeholder="/Documents/MiCarpeta"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && onSubmit()}
      />
      <Button variant="contained" onClick={onSubmit} disabled={!value.trim()}>
        Usar ruta
      </Button>
    </Box>
  </Box>
);

const FolderNavigator = ({
  loading,
  breadcrumbs,
  pathStack,
  folderItems,
  fileItems,
  allItems,
  onBreadcrumbNavigate,
  onSelectCurrentFolder,
  onNavigateInto,
  onSelectFolder,
}) => (
  <Box>
    {/* Breadcrumb */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      {pathStack.length > 0 && (
        <IconButton size="small" onClick={() => onBreadcrumbNavigate(pathStack.length - 2)}>
          <ArrowBack fontSize="small" />
        </IconButton>
      )}
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ flex: 1 }}>
        {breadcrumbs.map((crumb, i) =>
          i < breadcrumbs.length - 1 ? (
            <Link
              key={i}
              component="button"
              variant="body2"
              underline="hover"
              onClick={() => onBreadcrumbNavigate(i === 0 ? -1 : i - 1)}
            >
              {crumb.name}
            </Link>
          ) : (
            <Typography key={i} variant="body2" fontWeight={600}>
              {crumb.name}
            </Typography>
          )
        )}
      </Breadcrumbs>
      <Tooltip title="Seleccionar esta carpeta como raíz de importación">
        <Button
          size="small"
          variant="outlined"
          color="success"
          startIcon={<CheckCircleOutline />}
          onClick={onSelectCurrentFolder}
          disabled={loading}
        >
          Seleccionar esta carpeta
        </Button>
      </Tooltip>
    </Box>

    <Divider sx={{ mb: 1 }} />

    {loading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    ) : (
      <List dense sx={{ maxHeight: 380, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        {folderItems.map(folder => (
          <ListItem
            key={folder.id}
            button
            onClick={() => onNavigateInto(folder)}
            sx={{ '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Folder color="warning" />
            </ListItemIcon>
            <ListItemText
              primary={folder.name}
              secondary={folder.modifiedAt ? `Modificado: ${new Date(folder.modifiedAt).toLocaleDateString()}` : null}
            />
            <ListItemSecondaryAction>
              <Tooltip title="Seleccionar esta carpeta">
                <IconButton
                  size="small"
                  color="success"
                  onClick={e => { e.stopPropagation(); onSelectFolder(folder); }}
                >
                  <CheckCircleOutline fontSize="small" />
                </IconButton>
              </Tooltip>
              <NavigateNext fontSize="small" sx={{ color: 'text.secondary', verticalAlign: 'middle' }} />
            </ListItemSecondaryAction>
          </ListItem>
        ))}

        {fileItems.map(file => (
          <ListItem key={file.id} sx={{ opacity: 0.65 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <InsertDriveFile color="action" fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={file.name} primaryTypographyProps={{ variant: 'body2' }} />
          </ListItem>
        ))}

        {allItems.length === 0 && (
          <ListItem>
            <ListItemText primary="Carpeta vacía" sx={{ textAlign: 'center', color: 'text.secondary' }} />
          </ListItem>
        )}
      </List>
    )}

    {fileItems.length > 0 && folderItems.length === 0 && (
      <Alert severity="info" sx={{ mt: 1 }}>
        Esta carpeta contiene {fileItems.length} archivo{fileItems.length !== 1 ? 's' : ''} y ninguna subcarpeta.
        Al seleccionarla, cada archivo se importará como un expediente.
      </Alert>
    )}
  </Box>
);

export default FolderBrowserStep;
